import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { trpc } from './trpc';

/** Resolved mode for the mobile billing link-out flow (ADR-0093 — App Store
 *  / Play Store forbid selling the app's digital subscription via an
 *  in-app Stripe checkout on native platforms).
 *
 *  - `'native'` — run the real in-app Stripe flow. Always true on web;
 *    never true on iOS/Android.
 *  - `'link_out'` — the platform's normal native mode: don't run
 *    Checkout / changePlan in-app, link out to the web subscribe page
 *    instead.
 *  - `'off'` — server-controlled kill-switch: hide the upgrade CTA
 *    entirely (drop to free-tier) without a client rebuild. */
export type MobileBillingMode = 'native' | 'link_out' | 'off';

/** How long the mobile billing config is considered fresh before
 *  react-query refetches in the background. This is a feature flag, not
 *  user data — a few minutes of staleness is fine, and it keeps the
 *  query cheap since every upgrade surface reads it. */
const STALE_TIME_MS = 5 * 60 * 1000;

/** Read the web app origin from Expo config extra (wired by
 *  app.config.js). Falls back to '' when unavailable (e.g. under
 *  Vitest, where Constants.expoConfig is typically mocked or absent). */
function getWebUrl(): string {
  const webUrl = Constants.expoConfig?.extra?.webUrl;
  return typeof webUrl === 'string' ? webUrl : '';
}

function buildUpgradeUrl(webUrl: string): string {
  if (!webUrl) return '/pricing';
  return `${webUrl.replace(/\/+$/, '')}/pricing`;
}

/**
 * Resolves the mobile billing mode and the URL to link out to when
 * that mode is `'link_out'`.
 *
 * Web always reports `'native'` — the real in-app Stripe flow is
 * unaffected, this hook exists purely to steer native. On iOS/Android,
 * the mode comes from the server's `billing.mobileConfig` kill-switch
 * (`MOBILE_BILLING_IOS` / `MOBILE_BILLING_ANDROID`), read once and
 * cached for `STALE_TIME_MS`. While the query is loading, or if it
 * errors, we default to `'link_out'` — a blip in fetching the config
 * must never trap a native user on a dead free-tier with no upgrade
 * path (fail toward showing the CTA + web link, never toward silently
 * hiding it).
 */
export function useMobileBillingMode(): { mode: MobileBillingMode; upgradeUrl: string } {
  const upgradeUrl = buildUpgradeUrl(getWebUrl());

  // Web keeps the existing in-app Stripe flow untouched — don't even
  // issue the config query.
  const isNativePlatform = Platform.OS === 'ios' || Platform.OS === 'android';

  const config = trpc.billing.mobileConfig.useQuery(undefined, {
    enabled: isNativePlatform,
    staleTime: STALE_TIME_MS,
  });

  if (!isNativePlatform) {
    return { mode: 'native', upgradeUrl };
  }

  if (config.isLoading || config.isError || !config.data) {
    return { mode: 'link_out', upgradeUrl };
  }

  const platformMode = Platform.OS === 'ios' ? config.data.ios : config.data.android;
  return { mode: platformMode, upgradeUrl };
}
